import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Activity, AlertTriangle, BarChart3, Download, Loader2,
  Presentation, RefreshCw, Scissors, Sparkles, TrendingUp, X,
} from "lucide-react";
import { toast } from "sonner";

import AnalyticsFilters from "./analytics/AnalyticsFilters";
import AnalyticsTabs from "./analytics/AnalyticsTabs";
import PresentationOverlay from "./analytics/PresentationOverlay";
import { Kpi, InsightBody } from "./analytics/widgets";
import { brl, num, monthLabel, WEEKDAY_ORDER, EMPTY_FILTERS } from "./analytics/utils";
import ErrorBoundary from "@/components/ErrorBoundary";

/**
 * Top-level orchestrator: owns filters, fetched data, AI insight, active
 * tab, and presentation toggle. The heavy rendering lives in:
 *   - AnalyticsFilters    pills + filter card
 *   - AnalyticsTabs       6 inline tab panels with charts
 *   - PresentationOverlay full-screen meeting mode
 *   - widgets.jsx         shared Kpi / ChartCard / Empty / RankingCard / Heatmap / InsightBody
 */
export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({ surgeons: [], anesths: [], techs: [], hospitals: [], procedures: [] });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [activePill, setActivePill] = useState("all");
  const [insight, setInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("geral");
  const [presentation, setPresentation] = useState(false);

  function openPresentation() {
    setPresentation(true);
    try { document.documentElement.requestFullscreen?.(); } catch (e) { /* non-fatal */ }
  }
  function closePresentation() {
    setPresentation(false);
    if (document.fullscreenElement) {
      try { document.exitFullscreen?.(); } catch (e) { /* non-fatal */ }
    }
  }

  async function explainWithAI() {
    if (!data || insightLoading) return;
    if ((data?.totals?.cases_total ?? 0) === 0) {
      toast.error("Nada para analisar — filtre um período com dados primeiro.");
      return;
    }
    setInsightLoading(true);
    try {
      const r = await api.post("/ai/analytics/explain", { snapshot: data });
      setInsight(r.data);
      toast.success("Análise gerada");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setInsightLoading(false); }
  }

  useEffect(() => {
    Promise.all([
      api.get("/surgeons").then((r) => r.data || []).catch(() => []),
      api.get("/anesthesiologists").then((r) => r.data || []).catch(() => []),
      api.get("/instrument-technicians").then((r) => r.data || []).catch(() => []),
      api.get("/hospitals").then((r) => r.data || []).catch(() => []),
      api.get("/procedures").then((r) => r.data || []).catch(() => []),
    ]).then(([surgeons, anesths, techs, hospitals, procedures]) => {
      setOptions({ surgeons, anesths, techs, hospitals, procedures });
    });
  }, []);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v === "" || v === null || v === undefined) return;
        params[k] = v;
      });
      const r = await api.get("/analytics", { params });
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); setInsight(null); }, [filters]);

  function clearFilters() {
    setFilters(EMPTY_FILTERS);
    setActivePill("all");
  }

  const t = data?.totals || {};

  const monthSeries = useMemo(() => (data?.by_month || []).map((m) => ({
    ...m,
    label: monthLabel(m.month),
  })), [data]);

  const pieShift = useMemo(() => {
    const labels = { manha: "Manhã", tarde: "Tarde", noite: "Noite" };
    return (data?.by_period || [])
      .filter((p) => p.period !== "—")
      .map((p) => ({ name: labels[p.period] || p.period, value: p.total }));
  }, [data]);

  const pieSusp = useMemo(
    () => (data?.suspensions_by_reason || []).map((s) => ({ name: s.label, value: s.total })),
    [data]
  );

  const weekdaySeries = useMemo(() => {
    const map = Object.fromEntries((data?.by_weekday || []).map((w) => [w.weekday, w.total]));
    return WEEKDAY_ORDER.map((w) => ({ weekday: w, total: map[w] || 0 }));
  }, [data]);

  const topSurgeons = (data?.by_surgeon || []).slice(0, 10);
  const topProcs = (data?.by_procedure || []).slice(0, 10);
  const topAnesth = (data?.by_anesthesiologist || []).slice(0, 10);
  const topHospitals = (data?.by_hospital || []).slice(0, 10);

  return (
    <>
      <div className="space-y-5" data-testid="analytics-page">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
              <BarChart3 className="h-7 w-7 text-blue-700" /> BI / Análise
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Dashboard executivo para diretoria. Filtre por período, pessoa, procedimento, turno e muito mais.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <Button
              size="sm"
              onClick={explainWithAI}
              disabled={insightLoading || !data}
              className="bg-blue-700 hover:bg-blue-800"
              data-testid="btn-explain-ai"
            >
              {insightLoading
                ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Analisando…</>
                : <><Sparkles className="h-4 w-4 mr-1" /> Explicar esses números</>}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={openPresentation}
              disabled={!data}
              className="border-slate-300"
              data-testid="btn-presentation"
            >
              <Presentation className="h-4 w-4 mr-1" /> Modo reunião
            </Button>
            <Button variant="outline" size="sm" onClick={clearFilters} data-testid="btn-clear-filters">
              <RefreshCw className="h-4 w-4 mr-1" /> Limpar
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.print()}>
              <Download className="h-4 w-4 mr-1" /> Imprimir / PDF
            </Button>
          </div>
        </div>

        <AnalyticsFilters
          filters={filters}
          setFilters={setFilters}
          options={options}
          loading={loading}
          activePill={activePill}
          setActivePill={setActivePill}
          onClear={clearFilters}
        />

        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
          <Kpi icon={Activity} tone="blue" label="Casos" value={num(t.cases_total)} testid="kpi-total" />
          <Kpi icon={Scissors} tone="blue" label="Procedimentos" value={num(t.procedures_performed)} testid="kpi-procs" />
          <Kpi icon={TrendingUp} tone="green" label="Executadas" value={num(t.cases_executed)} testid="kpi-executed" />
          <Kpi icon={AlertTriangle} tone="amber" label="Suspensas" value={num(t.cases_suspended)} testid="kpi-suspended" />
          <Kpi icon={TrendingUp} tone="slate" label="Taxa exec." value={`${t.execution_rate ?? 0}%`} testid="kpi-exec-rate" />
          <Kpi icon={TrendingUp} tone="slate" label="Aprovado" value={brl(t.approved)} testid="kpi-approved" />
          <Kpi icon={TrendingUp} tone="blue" label="Faturado" value={brl(t.billed)} testid="kpi-billed" />
          <Kpi icon={TrendingUp} tone="green" label="Recebido" value={brl(t.received)} testid="kpi-received" />
        </div>

        {insight && (
          <Card className="border-blue-200 bg-blue-50/30 shadow-sm" data-testid="ai-insight-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-md bg-blue-700 flex items-center justify-center">
                    <Sparkles className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h2 className="font-display text-base font-semibold text-slate-900">Análise executiva por IA</h2>
                    <p className="text-xs text-slate-500">
                      Gerado por {insight.model} · {insight.generated_at ? new Date(insight.generated_at).toLocaleString("pt-BR") : ""}
                    </p>
                  </div>
                </div>
                <button onClick={() => setInsight(null)} className="text-slate-400 hover:text-slate-700" data-testid="btn-close-insight">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <InsightBody text={insight.narrative} />
              <div className="mt-3 text-[11px] text-slate-400">
                A IA analisa apenas o snapshot atual dos filtros. Mudanças de filtro ocultam esta análise — gere de novo quando quiser.
              </div>
            </CardContent>
          </Card>
        )}

        <ErrorBoundary inline label="Gráficos indisponíveis para o período" testid="analytics-tabs-error">
          <AnalyticsTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            data={data}
            totals={t}
            monthSeries={monthSeries}
            pieShift={pieShift}
            pieSusp={pieSusp}
            weekdaySeries={weekdaySeries}
            topSurgeons={topSurgeons}
            topProcs={topProcs}
            topAnesth={topAnesth}
            topHospitals={topHospitals}
          />
        </ErrorBoundary>

        <div className="text-[11px] text-slate-400 text-right">
          Gerado em {data?.generated_at ? new Date(data.generated_at).toLocaleString("pt-BR") : "—"} · tire print ou imprima como PDF pelo botão acima.
        </div>
      </div>

      <PresentationOverlay
        open={presentation}
        onClose={closePresentation}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        data={data}
        totals={t}
        monthSeries={monthSeries}
        pieShift={pieShift}
        pieSusp={pieSusp}
        weekdaySeries={weekdaySeries}
        topSurgeons={topSurgeons}
        topProcs={topProcs}
        topAnesth={topAnesth}
        topHospitals={topHospitals}
        filters={filters}
      />
    </>
  );
}
